import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import API, { UPLOADS_BASE_URL } from "../services/api";
import Loader from "../components/Loader";
import { FaSearch, FaFileExcel, FaChartBar, FaFilter } from "react-icons/fa";

function ProfilesReport() {
    const [profiles, setProfiles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    
    // Filter states
    const [name, setName] = useState("");
    const [mobile, setMobile] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [createdByFilter, setCreatedByFilter] = useState("");
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProfiles, setTotalProfiles] = useState(0);
    const limit = 10;

    // =====================================
    // FETCH REPORT DATA
    // =====================================
    const fetchReport = async (currentPage = 1) => {
        try {
            setLoading(true);
            let url = `/profile/report?page=${currentPage}&limit=${limit}`;

            if (name.trim()) url += `&name=${encodeURIComponent(name.trim())}`;
            if (mobile.trim()) url += `&mobile=${encodeURIComponent(mobile.trim())}`;
            if (departmentFilter) url += `&department=${encodeURIComponent(departmentFilter)}`;
            if (createdByFilter) url += `&createdBy=${createdByFilter}`;

            const response = await API.get(url);
            setProfiles(response.data.profiles);
            setTotalProfiles(response.data.totalProfiles);
            setTotalPages(response.data.totalPages || 1);
        } catch (error) {
            console.error("Failed to fetch report data", error);
        } finally {
            setLoading(false);
        }
    };

    // =====================================
    // FETCH FILTER OPTIONS
    // =====================================
    const fetchFilterOptions = async () => {
        try {
            // Fetch departments
            const depResponse = await API.get("/department/list");
            setDepartments(depResponse.data.departments);

            // Fetch users (creator list)
            const userResponse = await API.get("/auth/users");
            setUsers(userResponse.data.users);
        } catch (error) {
            console.error("Failed to fetch filter options", error);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        fetchReport(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, departmentFilter, createdByFilter]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        fetchReport(1);
    };

    // =====================================
    // EXPORT FILTERED REPORT
    // =====================================
    const handleExportReport = async () => {
        try {
            let url = "/profile/export?";
            if (name.trim()) url += `&name=${encodeURIComponent(name.trim())}`;
            if (mobile.trim()) url += `&mobile=${encodeURIComponent(mobile.trim())}`;
            if (departmentFilter) url += `&department=${encodeURIComponent(departmentFilter)}`;
            if (createdByFilter) url += `&createdBy=${createdByFilter}`;

            const response = await API.get(url, { responseType: "blob" });
            const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.setAttribute("download", `profiles_report_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Export report failed", error);
            alert("Failed to export report.");
        }
    };

    return (
        <div className="main-wrapper">
            <Sidebar />

            <div className="content-wrapper">
                {/* HEADER */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4 animate-fade-in">
                    <div>
                        <h2 className="fw-extrabold text-slate mb-1">Profiles Report</h2>
                        <p className="text-muted mb-0">Generate filtered reports of employee profiles</p>
                    </div>

                    <button
                        className="btn btn-success d-flex align-items-center gap-2 shadow-sm"
                        onClick={handleExportReport}
                    >
                        <FaFileExcel size={14} /> Export Report
                    </button>
                </div>

                {/* FILTERS CARD */}
                <div className="card border-0 shadow-sm mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                    <div className="card-body p-3">
                        <form onSubmit={handleSearchSubmit} className="row g-3">
                            {/* Profile Name */}
                            <div className="col-12 col-md-3">
                                <label className="form-label text-muted small fw-semibold">Profile Name</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                        <FaSearch size={12} />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Search name..."
                                        className="form-control border-0 shadow-none bg-transparent"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Mobile Number */}
                            <div className="col-12 col-md-3">
                                <label className="form-label text-muted small fw-semibold">Mobile Number</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                        <FaSearch size={12} />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Search mobile..."
                                        className="form-control border-0 shadow-none bg-transparent"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Department Filter */}
                            <div className="col-6 col-md-2">
                                <label className="form-label text-muted small fw-semibold">Department</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                        <FaFilter size={12} />
                                    </span>
                                    <select
                                        className="form-select border-0 shadow-none bg-transparent"
                                        value={departmentFilter}
                                        onChange={(e) => {
                                            setDepartmentFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <option value="">All Departments</option>
                                        {departments.map((dep) => (
                                            <option key={dep._id} value={dep.name}>
                                                {dep.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Created By Filter */}
                            <div className="col-6 col-md-2">
                                <label className="form-label text-muted small fw-semibold">Created By</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                        <FaFilter size={12} />
                                    </span>
                                    <select
                                        className="form-select border-0 shadow-none bg-transparent"
                                        value={createdByFilter}
                                        onChange={(e) => {
                                            setCreatedByFilter(e.target.value);
                                            setPage(1);
                                        }}
                                    >
                                        <option value="">All Creators</option>
                                        {users.map((u) => (
                                            <option key={u._id} value={u._id}>
                                                {u.name} ({u.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="col-12 col-md-2 d-flex align-items-end">
                                <button type="submit" className="btn btn-dark w-100 py-2">
                                    Apply Filter
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {loading ? (
                    <Loader message="Loading report data..." />
                ) : (
                    <>
                        {/* TABLE CARD */}
                        <div className="card border-0 shadow-sm animate-fade-in" style={{ animationDelay: "0.2s" }}>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4" style={{ width: "60px" }}>#</th>
                                        <th style={{ width: "80px" }}>Photo</th>
                                        <th>Name</th>
                                        <th>Department</th>
                                        <th>Mobile</th>
                                        <th>Created By</th>
                                        <th className="pe-4">Created At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {profiles.length > 0 ? (
                                        profiles.map((profile, index) => (
                                            <tr key={profile._id} className="hover-bg-light">
                                                <td className="ps-4 text-muted font-monospace">
                                                    {((page - 1) * limit) + index + 1}
                                                </td>
                                                <td>
                                                    <img
                                                        src={
                                                            profile.images?.length > 0
                                                                ? `${UPLOADS_BASE_URL}/${profile.images[0]}`
                                                                : "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=100&h=100&q=80"
                                                        }
                                                        alt="profile"
                                                        width="44"
                                                        height="44"
                                                        className="shadow-sm"
                                                        style={{
                                                            borderRadius: "50%",
                                                            objectFit: "cover",
                                                            border: "2px solid #ffffff"
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="fw-bold text-dark">
                                                        {profile.personalDetails?.name || "N/A"}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span 
                                                        className="badge px-3 py-2 text-capitalize"
                                                        style={{
                                                            background: "rgba(79, 70, 229, 0.08)",
                                                            color: "#4f46e5",
                                                            fontWeight: "600"
                                                        }}
                                                    >
                                                        {profile.professionalDetails?.department || "General"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="font-monospace text-muted">
                                                        {profile.personalDetails?.mobile || "N/A"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="fw-bold text-slate">
                                                        {profile.createdBy?.name || "System"}
                                                    </div>
                                                    <div className="text-muted small">
                                                        {profile.createdBy?.email || "Pre-seeded"}
                                                    </div>
                                                </td>
                                                <td className="pe-4 text-muted small font-monospace">
                                                    {new Date(profile.createdAt).toLocaleDateString()}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5 text-muted">
                                                <FaChartBar className="text-muted mb-3" size={32} />
                                                <h5>No Matching Profiles Found</h5>
                                                <p className="small mb-0">Try adjusting your filters or search terms.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4 animate-fade-in">
                        <span className="text-muted small">
                            Showing {profiles.length} of {totalProfiles} profiles
                        </span>
                        <div className="d-flex align-items-center gap-2">
                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index}
                                    className={`btn d-flex align-items-center justify-content-center font-semibold`}
                                    style={{
                                        width: "40px",
                                        height: "40px",
                                        padding: "0",
                                        borderRadius: "50%",
                                        backgroundColor: page === index + 1 ? "var(--primary-color)" : "#ffffff",
                                        color: page === index + 1 ? "#ffffff" : "var(--text-muted)",
                                        border: page === index + 1 ? "none" : "1px solid var(--border-light)",
                                        boxShadow: page === index + 1 ? "0 4px 10px rgba(79, 70, 229, 0.2)" : "none"
                                    }}
                                    onClick={() => setPage(index + 1)}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                    </>
                )}
            </div>
        </div>
    );
}

export default ProfilesReport;
